<script setup lang="ts">
/**
 * MY — Share Certificate (M32205) 문서-뷰 패널.
 * Transfer Share Certificate 화면만 분리한 단독 Studio.
 */
import { ref, reactive, computed, provide, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import {
  labelize, typeOf, SHARE_CERT_REQUIRED, sharesToWords,
} from './shareCertificate/shareCertFields';
import { SHARE_CERT_DOC_CTX } from './shareCertificate/shareCertContext';
import {
  buildShareCertVariables,
  emptyShareCertState,
  type ShareCertState,
} from '../mappers/shareCertMapper';
import { formatMyAddressLine } from '@/shared/utils/malaysianAddress';
import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import AddressInputModal from './cdd/AddressInputModal.vue';
import ShareCertDoc from './shareCertificate/ShareCertDoc.vue';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  appliedAddress?: { key: string; line: string; town: string; state: string; postcode: string; country: string; seq: number } | null;
  pendingPick?: { value: string; seq: number } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const entityCd = computed(() => String(props.entityData?.entityCd || props.entityData?.icpCd || '').trim());

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());

const state = reactive<ShareCertState>(emptyShareCertState());
const SIGNER_KEY = 'share_cert';
type ShareRow = {
  contactCd: string;
  certificateNo: string;
  numberOfShares: number;
  shareType: string;
};

const activeKey = ref('');
const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);
const entityShareRows = ref<ShareRow[]>([]);
const holderContactCd = ref('');

function syncShareCount(sharesRaw: string) {
  const shares = String(sharesRaw || '').trim();
  state.values.no_of_shares = shares;
  state.values.shares_numeral = shares;
  state.values.shares_words = sharesToWords(shares);
}

function setFieldValue(key: string, val: string) {
  if (key === 'cert_no') {
    selectCertNo(String(val || ''));
    return;
  }
  if (key === 'no_of_shares' || key === 'shares_numeral') {
    syncShareCount(val);
    return;
  }
  state.values[key] = val;
}

function getShareCertsForContact(contactCd: string): Array<{ certificateNo: string; shares: number; shareType: string }> {
  const cc = String(contactCd || '').trim();
  if (!cc) return [];
  return entityShareRows.value
    .filter((r) => String(r.contactCd || '').trim() === cc)
    .filter((r) => (Number(r.numberOfShares ?? 0) || 0) > 0 || String(r.certificateNo || '').trim())
    .map((r) => ({
      certificateNo: String(r.certificateNo || '').trim(),
      shares: Number(r.numberOfShares ?? 0) || 0,
      shareType: String(r.shareType || '').trim(),
    }))
    .filter((r) => !!r.certificateNo)
    .sort((a, b) => a.certificateNo.localeCompare(b.certificateNo, undefined, { numeric: true, sensitivity: 'base' }));
}

function certOptions(): Array<{ title: string; value: string; shares: number }> {
  return getShareCertsForContact(holderContactCd.value).map((c) => ({
    title: `Cert No. ${c.certificateNo} (${c.shares.toLocaleString()} shares)`,
    value: c.certificateNo,
    shares: c.shares,
  }));
}

function selectCertNo(certNo: string) {
  const v = String(certNo || '').trim();
  if (!v) {
    state.values.cert_no = '';
    return;
  }
  const hit = getShareCertsForContact(holderContactCd.value).find((c) => c.certificateNo === v);
  if (!hit) {
    state.values.cert_no = v;
    return;
  }
  state.values.cert_no = hit.certificateNo;
  syncShareCount(String(hit.shares || ''));
}

function activate(key: string) {
  activeKey.value = key;
  const t = typeOf(key);
  if (t === 'person' || t === 'entity') {
    emit('request-person-lookup', {
      fieldGroup: 'share-certificate',
      key,
      defaultRole: key === 'member_name' ? 'SHAREHOLDER' : null,
      lookupType: key === 'company_name' || t === 'entity' ? 'entity' : 'officer',
    });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = state.values[key] || '';
    if (t === 'date') dateMenu.value = true;
  }
}
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (inputKey.value) setFieldValue(inputKey.value, inputText.value.trim());
  inputKey.value = ''; activeKey.value = '';
}
function skipInput() { inputKey.value = ''; activeKey.value = ''; }

const pendingPickValue = ref('');
watch(() => props.pendingPick, (p) => { pendingPickValue.value = p?.value || ''; });
function placePick(key: string): boolean {
  if (!pendingPickValue.value) return false;
  setFieldValue(key, pendingPickValue.value);
  pendingPickValue.value = '';
  return true;
}

const addrModal = ref(false);
const addrKey = ref('');
const addrLabel = ref('');
const addrInitial = ref<{ line?: string }>({});
function openAddress(key: string) {
  if (props.studio) { addrKey.value = key; emit('request-address-input', key); return; }
  addrKey.value = key;
  addrLabel.value = labelize(key);
  addrInitial.value = { line: state.values[key] || '' };
  addrModal.value = true;
}
function applyAddressTo(key: string, a: { line: string; town: string; state: string; postcode: string; country: string }) {
  if (!key) return;
  state.values[key] = formatMyAddressLine([
    a.line,
    [a.postcode, a.town].filter(Boolean).join(' '),
    a.state,
    a.country,
  ]);
}
function applyAddress(a: { line: string; town: string; state: string; postcode: string; country: string }) {
  applyAddressTo(addrKey.value, a);
}
watch(() => props.appliedAddress, (a) => { if (a?.key) applyAddressTo(a.key, a); });

function signersOf(varKey: string): SignerEntry[] { return state.signers[varKey] || []; }
function setSigners(varKey: string, list: SignerEntry[]) {
  state.signers[varKey] = Array.isArray(list) ? list : [];
}

function applyPersonToField(key: string, a: any) {
  const name = a.name || a.contactName || [a.firstName, a.middleName, a.surname].filter(Boolean).join(' ') || '';
  state.values[key] = name;
  if (key === 'member_name') {
    holderContactCd.value = String(a.contactCd || a.CONTACT_CD || '').trim();
    state.values.member_contact_cd = holderContactCd.value;
    const opts = certOptions();
    if (opts.length === 1) {
      selectCertNo(opts[0].value);
    } else if (opts.length > 1) {
      const keep = String(state.values.cert_no || '').trim();
      if (!keep || !opts.some((o) => o.value === keep)) {
        state.values.cert_no = '';
        state.values.no_of_shares = '';
        state.values.shares_numeral = '';
        state.values.shares_words = '';
      }
    }
  }
}

watch(() => props.selectedPerson, (p) => {
  const ctx = props.activePersonContext;
  if (!p || !ctx || ctx.fieldGroup !== 'share-certificate' || !ctx.key) return;
  applyPersonToField(ctx.key, p as any);
});

provide(SHARE_CERT_DOC_CTX, {
  valueOf: (k) => state.values[k] || '',
  labelOf: labelize,
  typeOf,
  activeKey,
  activate,
  setFieldValue,
  openAddress,
  entityCd,
  signersOf,
  setSigners,
  pendingPickValue,
  placePick,
  certOptions,
  selectCertNo,
});

const entityPrefilled = ref('');
watch(
  () => [entityCd.value, props.entityData] as const,
  async ([cd]) => {
    if (!cd || entityPrefilled.value === cd) return;
    entityPrefilled.value = cd;
    const put = (k: string, v: any, force = false) => {
      const s = String(v || '').trim();
      if (!s) return;
      if (force || !String(state.values[k] || '').trim()) state.values[k] = s;
    };
    put('company_name', props.entityData?.companyName || props.entityData?.entityName, true);
    put('company_registration_no', props.entityData?.registrationNo, true);
    try {
      const addrs = await fetchEntityAddresses(cd);
      const gov = (addrs as any)?.government || addrs;
      if (gov) {
        put('company_name', gov.companyName, false);
        put('company_registration_no', gov.registrationNo || gov.regNo, false);
        const reg = formatMyAddressLine(String(gov.registered || gov.registeredOffice || ''));
        put('registered_office_address', reg, false);
      }
    } catch { /* noop */ }
    try {
      const shRes = await fetch(`/api/entity_my/shares/${encodeURIComponent(cd)}`, { credentials: 'include' });
      if (!shRes.ok) {
        entityShareRows.value = [];
      } else {
        const shJson: any = await shRes.json();
        const shData = shJson?.data?.shares ? shJson.data : (shJson?.data?.data ?? shJson?.data ?? shJson ?? {});
        const shares = Array.isArray(shData?.shares) ? shData.shares : [];
        entityShareRows.value = shares.map((s: any) => ({
          contactCd: String(s.contactCd || '').trim(),
          certificateNo: String(s.certificateNo || s.certiNo || '').trim(),
          numberOfShares: Number(s.numberOfShares ?? s.acqNumberOfShares ?? 0) || 0,
          shareType: String(s.shareType || ''),
        }));
      }
    } catch {
      entityShareRows.value = [];
    }
  },
  { immediate: true },
);

const overallProgress = computed(() => {
  const list = SHARE_CERT_REQUIRED;
  if (!list.length) return 0;
  const filled = list.filter((k) => String(state.values[k] || '').trim()).length;
  return Math.round((filled / list.length) * 100);
});
watch(overallProgress, (v) => emit('progress-change', v), { immediate: true });

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });
onMounted(async () => {
  const code = props.selection?.task?.TASK_CODE;
  if (code) {
    try {
      const cfg = await getTaskConfigByCode(code);
      taskConfigSteps.value = (cfg as any)?.processSteps || [];
    } catch { /* noop */ }
  }
});

const availableTemplates = computed(() => (props.selection?.documents || []).filter((d: any) => d.tmpId));

function tmplName(d: any): string {
  return String(d.templateName || d.docNm || d.name || d.fileName || d.orgFileName || d.docCode || '');
}
function templateFileLabel(d: any, fallback: string): string {
  const raw = tmplName(d).trim() || fallback;
  return raw.replace(/\.docx$/i, '').trim() || fallback;
}
function resolveTemplate(): { tmpId: number; label: string } | null {
  const docs = availableTemplates.value as any[];
  if (!docs.length) return null;
  const re = /share\s*certificate|certificate\s*of\s*shares/i;
  const byName = docs.find((d) => re.test(tmplName(d)));
  const hit = byName || docs[0];
  if (!hit?.tmpId) return null;
  return {
    tmpId: hit.tmpId,
    label: templateFileLabel(hit, 'Share Certificate'),
  };
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }

  const hit = resolveTemplate();
  if (!hit) {
    progress.value = '⚠ 연결된 문서 템플릿이 없습니다. 관리자에서 Share Certificate 문서 템플릿을 연결해 주세요.';
    return;
  }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      presetForm: gatherState(),
      companyName: state.values.company_name || '',
      companyRegistrationNo: state.values.company_registration_no || '',
      memberName: state.values.member_name || '',
      memberContactCd: state.values.member_contact_cd || '',
      certNo: state.values.cert_no || '',
      issueDate: state.values.issue_date || '',
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value, taskFlow, officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false; return;
      }
    }

    progress.value = 'Preparing document…';
    const formData = buildShareCertVariables(state);
    const docSigners = state.signers[SIGNER_KEY] || [];
    try {
      const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          taskId,
          documents: [{
            templateId: hit.tmpId,
            label: hit.label,
            personName: String(state.values.member_name || state.values.company_name || 'Member'),
            entityCd: entityCd.value || undefined,
            formData,
            signers: docSigners.length ? docSigners : undefined,
          }],
          replaceExisting: editMode.value,
        }),
      }).then((r) => r.json());
      if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
    } catch (err: any) {
      console.warn('[Share Certificate] document save failed:', err);
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }
    try {
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(`presetTaskDetailPrefetch:${taskId}`, JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }));
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[Share Certificate] create task 실패', e);
    progress.value = `Error: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

function gatherState() {
  return {
    state: JSON.parse(JSON.stringify(state)),
    companyName: state.values.company_name || '',
    companyRegistrationNo: state.values.company_registration_no || '',
    memberName: state.values.member_name || '',
    memberContactCd: state.values.member_contact_cd || '',
    certNo: state.values.cert_no || '',
    issueDate: state.values.issue_date || '',
  };
}
function restoreState(s: any) {
  const src = s?.state || s?.presetForm?.state;
  if (src && typeof src === 'object') {
    const base = emptyShareCertState();
    Object.assign(state, { ...base, ...src });
    state.values = { ...base.values, ...(src.values || {}) };
    state.signers = { ...(src.signers || {}) };
    holderContactCd.value = String(state.values.member_contact_cd || '').trim();
  }
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });
</script>

<template>
  <div class="otc-panel" :class="{ 'otc-studio': studio }">
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Share Certificate</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')"><v-icon start size="16">mdi-arrow-left</v-icon>Back</v-btn>
    </div>

    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">Task Title</span>
      <v-text-field v-model="titleModel" placeholder="e.g. Share Certificate — Tilkoblet" variant="outlined" density="compact" hide-details class="ct-input" />
    </div>

    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ entityData?.companyName || entityData?.entityName || 'Entity' }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">Change</v-btn>
    </div>

    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">{{ labelize(inputKey) }} <span v-if="typeOf(inputKey) === 'date'" class="opt">(optional)</span></div>
      <div class="row">
        <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field v-bind="mp" v-model="inputText" placeholder="DD/MM/YYYY" variant="outlined" density="compact" hide-details append-inner-icon="mdi-calendar" />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field v-else v-model="inputText" :placeholder="labelize(inputKey)" variant="outlined" density="compact" hide-details autofocus @keydown.enter="commitInput" />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">Set</v-btn>
        <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">Skip</v-btn>
      </div>
    </div>

    <div class="co-doc-body">
      <section class="stage">
        <div class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">📄</span>
            <span class="nm">Share Certificate #1</span>
            <span class="no">Doc 1 / 1</span>
          </div>
          <div class="pkg-paper">
            <ShareCertDoc />
          </div>
        </div>
      </section>
    </div>

    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">Back</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: progress.startsWith('Error') || progress.includes('⚠') }">{{ progress }}</span>
        <v-btn
          v-if="!studio"
          color="primary"
          class="text-none"
          :loading="creating"
          :disabled="creating"
          :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
          @click="handleCreateTask"
        >{{ editMode ? 'Update Task' : 'Create Task' }}</v-btn>
      </div>
    </div>

    <AddressInputModal v-model="addrModal" :label="addrLabel" :has-components="false" :initial="addrInitial" @apply="applyAddress" />
  </div>
</template>

<style scoped>
.otc-panel { display:flex; flex-direction:column; }
.otc-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.otc-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; } .co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.ct-label { font-size:12px; font-weight:700; color:#5b6577; white-space:nowrap; } .ct-input { flex:1; }
.co-entity { display:flex; align-items:center; gap:8px; padding:10px 14px; border-bottom:1px solid #eef0f6; font-size:13px; font-weight:600; }
.co-inputbar { padding:10px 14px; background:#f7f9fc; border-bottom:1px solid #eef0f6; }
.co-inputbar .lbl { font-size:12px; font-weight:700; margin-bottom:6px; } .co-inputbar .opt { color:#8a94a6; font-weight:500; }
.co-inputbar .row { display:flex; gap:8px; align-items:center; }
.co-doc-body { padding:20px; background:#eceff5; }
.stage { width:100%; max-width:210mm; margin:0 auto; }
.pkg-doc { width:100%; margin:0 auto; }
.pkg-doclabel { display:flex; align-items:center; gap:7px; padding:0 4px 9px; }
.pkg-doclabel .ic { font-size:14px; line-height:1; }
.pkg-doclabel .nm { font-size:12px; font-weight:800; color:#42506a; letter-spacing:.01em; }
.pkg-doclabel .no { margin-left:auto; font-size:10.5px; font-weight:800; color:#9aa6bd; white-space:nowrap; }
/* TransferSharesPackageDoc 와 동일한 A4 paper shell */
.pkg-paper {
  width:210mm;
  min-height:297mm;
  max-width:100%;
  margin:0 auto;
  padding:18mm 20mm;
  box-sizing:border-box;
  background:#fff;
  box-shadow:0 4px 24px rgba(20, 30, 60, .12);
  font-family:Arial, 'Malgun Gothic', sans-serif;
  color:#111;
  font-size:12px;
  line-height:1.5;
  border-radius:2px;
  overflow:hidden;
}
.co-footer { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-top:1px solid #eef0f6; }
.co-foot-right { display:flex; align-items:center; gap:10px; margin-left:auto; }
.co-progress { font-size:12px; color:#5b6577; } .co-progress.err { color:#c62828; }
</style>
